import { ApolloCache, NormalizedCacheObject, split } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  InMemoryCache,
  Operation,
  StoreObject,
  defaultDataIdFromObject,
  from,
} from '@apollo/client';
import {
  GetCurrentUser,
  hasSSOCookies,
  logoutUserSSO,
  ResetUser,
} from '@converge/ui/domain/auth/auth-provider';
import {
  DocumentNode,
  OperationDefinitionNode,
  OperationTypeNode,
  print,
} from 'graphql';
import { config, convergeLogger } from '@converge/config';
import {
  customFetch,
  handleError,
  isArray,
  isNotNullOrUndefined,
  isString,
} from '@converge/utils';
import { getMainDefinition } from '@apollo/client/utilities';

import type { Contexts } from '@sentry/types';
import { KeyFieldsContext } from '@apollo/client/cache/inmemory/policies';
import { addBreadcrumb } from '@sentry/react';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import axios from 'axios';
import { hasTokenExpired } from '@converge/graphql';
import moment from 'moment';

const emptyCacheObj = {};

export class VoidCache extends ApolloCache<NormalizedCacheObject> {
  read() {
    return null;
  }
  write() {
    return undefined;
  }
  diff() {
    return {};
  }
  watch() {
    return () => {};
  }
  async reset() {}
  evict() {
    return false;
  }
  restore() {
    return this;
  }
  extract() {
    return emptyCacheObj;
  }
  removeOptimistic() {}
  override batch() {
    return undefined as any;
  }
  performTransaction() {}
  override recordOptimisticTransaction() {}
  override transformDocument(document: DocumentNode) {
    return document;
  }
  override transformForLink(document: DocumentNode) {
    return document;
  }
  override identify() {
    return undefined;
  }
  override gc() {
    return [] as string[];
  }
  override modify() {
    return false;
  }
  override readQuery() {
    return null;
  }
  override readFragment() {
    return null;
  }
  override writeQuery() {
    return undefined;
  }
  override writeFragment() {
    return undefined;
  }
  override updateQuery() {
    return null;
  }
  override updateFragment() {
    return null;
  }
}

const formatMessage = (
  operationType: OperationTypeNode,
  operation: Operation,
  elapsed?: number | string,
  elapsedIdleTimeMs?: number
): string[] => {
  const headerCss = [
    'color: gray; font-weight: lighter',
    `color: ${operationType === 'query' ? '#03A9F4' : 'red'};`,
    'color: inherit;', // operationName
  ];

  const parts = [
    '%c apollo',
    `%c${operationType}`,
    `%c${operation.operationName}`,
  ];

  if (operationType !== 'subscription' || typeof elapsed !== 'number') {
    parts.push(
      typeof elapsed === 'number'
        ? `%cfinished in ${elapsed} ms${
            typeof elapsedIdleTimeMs === 'number'
              ? `, log delayed ${elapsedIdleTimeMs} ms until browser idled`
              : ''
          }`
        : `%c${elapsed}`
    );
    const color =
      typeof elapsed === 'number' && elapsed > config.slowResponseThreshold
        ? 'red'
        : 'gray';
    headerCss.push(`color: ${color}; font-weight: lighter;`); // time

    parts.push(`at ${new Date()}`);
  }

  return [parts.join(' '), ...headerCss];
};

let REQUEST = 0;

const convergeLoggerLink = new ApolloLink((operation, forward) => {
  REQUEST++;
  const startTime = new Date().getTime();

  if (config.enableSentry) {
    addBreadcrumb({
      category: `gql req: ${operation.operationName}`,
      data: {
        request: REQUEST,
        variables: JSON.stringify(operation.variables),
        query: print(operation.query),
      },
      level: 'info',
      message: `GraphQL ${operation.operationName} started`,
      type: 'http',
    });
  }

  const operationTypes = (
    operation.query.definitions as OperationDefinitionNode[]
  ).map((definition) => definition.operation);

  const operationTypesSet = [...new Set(operationTypes)];

  const operationType = operationTypes[0];

  convergeLogger.info(...formatMessage(operationType, operation, 'started'));

  const context = operation.getContext();

  if (context['operationName']) {
    operation.setContext({
      headers: {
        'x-converge-operation-name': context['operationName'],
      },
    });
  }

  operation.setContext({
    headers: {
      'x-converge-env': config.convergeEnv,
      'x-converge-version': config.appVersionWithCommitHash,
      'x-converge-source': 'frontend',
      'x-converge-user':
        GetCurrentUser()?.user?.uuid?.replace(/-.*$/, '') ?? 'n/a',
    },
  });

  return forward(operation).map((result) => {
    const ellapsed = new Date().getTime() - startTime;

    const log = (ellapsedIdleTimeMs?: number) => {
      if (config.enableSentry) {
        addBreadcrumb({
          category: `gql res: ${operation.operationName}`,
          data: {
            request: REQUEST,
            ellapsed: `${ellapsed}ms`,
            errors: JSON.stringify(result.errors),
            data: JSON.stringify(result.data),
          },
          level: result.errors ? 'error' : 'info',
          message: `GraphQL ${operation.operationName} finished`,
          type: 'http',
        });
      }

      const group = formatMessage(
        operationType,
        operation,
        ellapsed,
        ellapsedIdleTimeMs
      );

      convergeLogger.group(...group);

      convergeLogger.log('INIT', operation);
      convergeLogger.log('RESP', result);

      if (ellapsed > config.slowResponseThreshold) {
        const slowResponseWarning = formatSlowResponseWarning(
          result,
          operation,
          operationTypesSet,
          ellapsed,
          ellapsedIdleTimeMs
        );

        convergeLogger.warn(...slowResponseWarning);
      }

      convergeLogger.groupEnd();
    };

    if (config.isNodeEnvDevelopment) {
      log();
    } else {
      // Wrapped in a setTimeout to ensure that this link is not blocking the UI thread in production
      if ('requestIdleCallback' in window) {
        const startIdleTime = new Date().getTime();
        requestIdleCallback(
          () => {
            const ellapsedIdleTime = new Date().getTime() - startIdleTime;
            log(ellapsedIdleTime);
          },
          {
            timeout: 1000,
          }
        );
      } else {
        setTimeout(log, 50);
      }
    }

    return result;
  });
});

const fetch = typeof window === 'undefined' ? global.fetch : customFetch;

const uploadLink = createUploadLink({
  uri: config.graphqlUrl,
  fetch: async (input, init) => {
    let resp: Response;

    try {
      resp = await fetch(input, {
        ...init,
      });
    } catch (cause) {
      if (!(cause instanceof Error)) {
        convergeLogger.error(cause);
        throw cause;
      }

      if (cause.name === 'AbortError') {
        convergeLogger.warn(`Request aborted.`);
      } else {
        convergeLogger.error(cause);
      }

      throw cause;
    }

    return resp;
  },
});

const refreshToken = async (): Promise<string> => {
  const user = GetCurrentUser();
  let token = null;
  if (user && user.refreshToken) {
    token = user.refreshToken;

    try {
      const response = await axios.post(
        config.refreshTokenUrl,
        {},
        {
          headers: {
            refresh_token: token,
          },
        }
      );

      const refreshTokenExpiryDate = moment(new Date())
        .utc()
        .add(30, 'days')
        .format('YYYY-MM-DDTHH:mm:ss');

      localStorage.setItem(
        'refreshTokenExpiryDate',
        JSON.stringify(refreshTokenExpiryDate)
      );

      return response?.data?.accessToken;
    } catch (error) {
      console.error('Error refreshing access token', error);
    }
  }
  return '';
};

const authLink = setContext(async ({ operationName }, { headers }) => {
  const user = GetCurrentUser();
  let token = null;
  const ssoCookies = hasSSOCookies();
  if (user && user.accessToken) {
    if (
      !ssoCookies &&
      user?.expiresEpoch &&
      hasTokenExpired(user?.expiresEpoch)
    ) {
      try {
        const accessToken = await refreshToken();
        if (!accessToken) {
          ResetUser();
          logoutUserSSO();
          return '';
        }
        token = accessToken;
      } catch {
        ResetUser();
        logoutUserSSO();
      }
    } else {
      token = user.accessToken;
    }
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const logoutLink = onError((context) => {
  if (
    context?.response?.errors?.find(
      (e) => e.message && e.message.indexOf('Authentication failed') > -1
    )
  ) {
    logoutUserSSO();
  }
});

class ConvergeFrontendGraphqlError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'ConvergeFrontendGraphqlError';
  }
}

const convergeOnErrorLink = onError((context) => {
  const errorMessage: any[] = [
    `GraphQL error in ${context.operation.operationName}:`,
  ];

  const error = context.networkError ?? context.graphQLErrors?.[0];

  const userValidationErrors = [
    'A cancellation midterm transaction already exists for this policy',
    'Cannot undo issuance: Policy is older than 30 days',
  ];

  const shouldSkipSentry =
    error?.message &&
    userValidationErrors.some((userError) =>
      error.message.toLowerCase().includes(userError.toLowerCase())
    );

  const graphqlQuery = context.operation.query;
  const graphqlQueryAsString = print(graphqlQuery);

  const sentryContext: Contexts = {
    graphqlErrors: (context.graphQLErrors ?? [])?.reduce(
      (acc, error, index) => {
        return {
          ...acc,
          [`Error ${index + 1}`]: {
            ...error,
            message: error.message,
            locations: JSON.stringify(error.locations),
          },
        };
      },
      {}
    ),
    operationVariables: context.operation.variables,
    operationInfo: {
      name: context.operation.operationName,
    },
    query: {
      body: graphqlQueryAsString,
    },
  };

  if (context.networkError) {
    sentryContext['networkError'] = {
      ...context.networkError,
      name: context.networkError.name,
      message: context.networkError.message,
      stack: context.networkError.stack,
    };
  }
  const genericErrors = [
    'unauthorized',
    'failed to fetch',
    'networkerror when attempting to fetch resource',
  ];

  const errorMsg = error?.message?.toLowerCase() ?? '';
  const matchedGenericError = genericErrors.find((e) => e === errorMsg);

  if (!shouldSkipSentry) {
    handleError({
      message: `GraphQL \`${
        context.operation.operationName ?? 'graphql operation'
      }\` operation failed`,
      source: 'Apollo Client',
      cause: new ConvergeFrontendGraphqlError(
        `GraphQL error${context.networkError ? ' (network error)' : ''} in ${
          context.operation.operationName
        }${error?.message ? `: ${error.message}` : ''}`
      ),
      context: sentryContext,
      fingerprint: [
        'graphql',
        matchedGenericError ||
          context.operation.operationName ||
          'graphql_operation',
        (matchedGenericError && error?.message) || '',
      ],
    });
  }

  if (context.networkError) {
    const networkErrorMessage: any[] = [];
    networkErrorMessage.push('Network error:');

    if (context.networkError.message) {
      networkErrorMessage.push(context.networkError.message);
    }

    if (context.networkError.name) {
      networkErrorMessage.push(`(${context.networkError.name})`);
    }

    if ('response' in context.networkError) {
      networkErrorMessage.push('\n');
      networkErrorMessage.push(
        `  Status: ${context.networkError.response.status}`
      );
      networkErrorMessage.push(`  Body: ${context.networkError.response.body}`);
    }

    if (context.networkError.stack) {
      networkErrorMessage.push(context.networkError.stack);
    }

    errorMessage.push(networkErrorMessage.join(' '));
  }

  let contextData: string;

  try {
    contextData = JSON.stringify(context, null, 2);
  } catch (e) {
    contextData = `Unable to stringify context: ${e}`;
  }

  convergeLogger.error(...errorMessage, context);

  if (context.graphQLErrors && context.graphQLErrors.length > 0) {
    convergeLogger.error(errorMessage[0], ...context.graphQLErrors);
  }

  if (context.networkError && 'response' in context.networkError) {
    convergeLogger.error(errorMessage[0], context.networkError.response);
  }

  convergeLogger.error(errorMessage[0], contextData);
});

const dataIdFromObject = (
  object: Readonly<StoreObject> | any,
  context: KeyFieldsContext
) => {
  const cacheId: any[] = [];

  const id =
    object['uuid'] ?? object['id'] ? object['uuid'] ?? object['id'] : null;

  if (object.__typename) {
    cacheId.push(object.__typename);
  }

  if (isNotNullOrUndefined(id)) {
    cacheId.push(id);
  }

  // TODO: Reminder to use relation UUIDs instead of node UUIDs
  if (object.__typename === 'Entity') {
    cacheId.push(Math.random() * 10);
  }

  if (
    object.__typename &&
    [
      'Fundamental',
      'IndustrySector',
      'Alias',
      'Address',
      'Contact',
      'Domain',
      'IpAddress',
      'Person',
      'Port',
      'Software',
      'SubDomain',
      'Vulnerability',
    ].includes(object.__typename)
  ) {
    if (isString(object['source'])) {
      cacheId.push(object['source']);
    } else if (isArray(object['source'])) {
      cacheId.push(...object['source']);
    }
  }

  if (
    object.__typename &&
    ['Coverage', 'Endorsement', 'Subjectivity', 'SubCoverage'].includes(
      object.__typename
    )
  ) {
    if (
      'quote' in object &&
      isNotNullOrUndefined(object['quote']) &&
      typeof object['quote'] === 'object' &&
      'uuid' in object['quote'] &&
      isString(object['quote']['uuid'])
    ) {
      cacheId.push(object['quote']['uuid']);
    }
  }

  const defaultId = defaultDataIdFromObject(object);

  if (defaultId) {
    cacheId.push(defaultId);
  }

  if (typeof object['node'] === 'object' && object['node']) {
    const nodeCacheId = dataIdFromObject(object['node'], context);
    if (nodeCacheId) {
      cacheId.push(nodeCacheId);
    } else if (isNotNullOrUndefined(object['cursor'])) {
      console.warn(`Using cursor for ${object.__typename}`, object);
      cacheId.push(['cursor', object['cursor']].join(':'));
    }
  }

  if (cacheId.length === 1 && cacheId[0] === object.__typename) {
    return false;
  }

  return cacheId.join(':');
};

export const useConvergeApolloClient = () => {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: config.graphqlUrl.replace(/^http/, 'ws'),
      connectionParams: async () => {
        const user = GetCurrentUser();
        return {
          authToken: user?.accessToken,
        };
      },
    })
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    from([
      convergeLoggerLink,
      convergeOnErrorLink,
      authLink,
      logoutLink,
      uploadLink,
    ])
  );

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      dataIdFromObject,
      typePolicies: {
        Query: {
          fields: {
            quotes: {
              keyArgs: ['filters'],
              merge: false,
            },
          },
        },
        PaymentExpenseType: { keyFields: false },
        PaymentSubExpenseType: { keyFields: false },
        PaymentLossType: { keyFields: false },
        PaymentCoverage: { keyFields: false },
      },
    }),
    defaultOptions: undefined,
  });
};

function formatSlowResponseWarning(
  result: FetchResult,
  operation: Operation,
  operationTypesSet: OperationTypeNode[],
  ellapsed: number,
  ellapsedIdleTimeMs: number | undefined
) {
  const byteSizeOfResponse = new TextEncoder().encode(
    JSON.stringify(result)
  ).length;
  const message: (string | number)[] = [];

  const styles: string[] = [];

  if (operation.operationName) {
    message.push('The GraphQL', operationTypesSet.join(', '), 'operation for');
    message.push(operation.operationName);
  } else {
    message.push('GraphQL', operationTypesSet.join(', '), 'operation');
  }

  message.push(
    'took',
    ellapsed,
    'ms for',
    (byteSizeOfResponse / 1000).toFixed(4),
    'KB of returned data',
    `(${[
      (byteSizeOfResponse / 1000 / (ellapsed / 1000)).toFixed(4),
      'KB per second',
    ].join(' ')}).`,
    'Consider optimizing this GraphQL request.'
  );

  if (navigator.connection) {
    message.push('The internet connection of the user is');

    if (navigator.connection.type) {
      message.push(navigator.connection.type);
    }

    if (navigator.connection.type && navigator.connection.effectiveType) {
      message.push('and');
    }

    if (navigator.connection.effectiveType) {
      message.push('rated as', navigator.connection.effectiveType);
    }

    message[message.length - 1] = `${message[message.length - 1]}.`;

    type Stat<V = any> = {
      name: string;
      value: V;
      unit: string;
      render?: (stat: Stat<V>) => string;
    };

    const stats: Stat[] = [
      {
        name: 'effective downlink speed',
        value: navigator.connection.downlink,
        unit: 'Mbps',
      },
      {
        name: 'max downlink speed',
        value: navigator.connection.downlinkMax,
        unit: 'Mbps',
      },
      {
        name: 'round-trip time',
        value: navigator.connection.rtt,
        unit: 'ms',
      },
      {
        name: 'data saving mode',
        value: navigator.connection.saveData,
        unit: 'enabled',
        render: (value: Stat<boolean>) =>
          `data saving mode is ${value.value ? 'on' : 'not on'}`,
      },
    ].filter((stat) => stat.value !== undefined && stat.value !== null);

    if (stats.length > 0) {
      message.push(
        `The browser is reporting that the user's ${stats
          .map(
            (stat, index) =>
              `${
                stat.render
                  ? stat.render(stat)
                  : ['the', stat.name, 'is', stat.value, stat.unit].join(' ')
              }${
                index === stats.length - 2
                  ? ', and that '
                  : index === stats.length - 1
                  ? ''
                  : ', '
              }`
          )
          .join('')}.`
      );
    }
  }

  if (typeof ellapsedIdleTimeMs === 'number') {
    message.push(
      `The request finished ${ellapsedIdleTimeMs} ms ago (this log message was delayed until the browser was idle).`
    );
  }

  return [message.join(' '), ...styles];
}
